import { ReactElement, useState } from 'react';
import { Component as CaretDownIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as CaretUpIcon } from 'icons/icon-ui--caret-up-outline.svg';
import { useQueryClient } from '@tanstack/react-query';
import styles from './ProductDisplayPage.module.scss';
import { MenuCartItem } from '../../models/menu.types';
import { ProductTile } from '../product-tile/ProductTile';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useNavigateWithUTMs } from '../../hooks/useNavigateWithUTMs';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import { useNotification } from '../../contexts/NotificationContext';

type Props = {
  recommendations: MenuCartItem[];
  header?: string;
};

export function Recommendations({
  recommendations,
  header,
}: Props): ReactElement {
  const [showRecommendations, setShowRecommendations] = useState(true);
  const toggleRecommendations = () => setShowRecommendations((prev) => !prev);

  const {
    cart,
    addToCart,
    updateCartQuantity,
    recalculateCart,
  } = useMenuWithCart();

  const { isLoggedIn } = useAuth();
  const { isActive } = useUser();
  const { navigate } = useNavigateWithUTMs();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const handleSoldOutLtoClick = () => {
    navigate('/lto/robin-arzon');
    window.scrollTo(0, 0);
  };

  const handleAddToCartWithToast = async (menuItem: MenuCartItem) => {
    if (cart?.isSkipped) {
      showNotification({
        content: <Toast variant={ToastVariant.CART_UNSKIPPED_AND_ADDED} />,
        duration: 5000,
      });
      await recalculateCart({
        ...cart,
        isSkipped: false,
      });
      queryClient.invalidateQueries({ queryKey: ['menus'] });
    }
    addToCart(menuItem);
  };

  return (
    <div className={styles.accordion_container} data-heap-id="upsell_module_you_may_also_like">
      <div
        role="button"
        tabIndex={0}
        onClick={toggleRecommendations}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleRecommendations();
          }
        }}
      >
        <div className={styles.dropdown}>
          <h3 className={styles.accordion_title}>{header ?? 'You May Also Like...'}</h3>
          {showRecommendations ? <CaretUpIcon width={23} /> : <CaretDownIcon width={23} />}
        </div>
      </div>
      <hr className={styles.accordion_divider} />
      {showRecommendations && (
        <div className={styles.scroll_container}>
          <div className={styles.recommendations_container}>
            {recommendations.map((menuItem: MenuCartItem, index: number) => {
              const quantityInCart = menuItem.quantity || 0;
              const showSoldOutOverlay = !menuItem.isInStock && quantityInCart === 0;
              return (
                <ProductTile
                  title={menuItem.title}
                  subTitle={menuItem.subtitle}
                  imageUrl={menuItem.imageURL}
                  totalPrice={menuItem.skuPrice}
                  isSaleTagEnabled={menuItem.saleTagEnabled}
                  salePrice={menuItem.salePrice}
                  pricePerServing={menuItem.pricePerServing}
                  salePricePerServing={menuItem.salePricePerServing}
                  servingSize={menuItem.servingSize}
                  quantity={menuItem.quantity || 0}
                  isInStock={menuItem.isInStock}
                  isActive={isActive}
                  badgeTags={menuItem.badgeTags}
                  cookTime={menuItem.cookTime}
                  brandName={menuItem.brandName}
                  unitSize={menuItem.unitSize}
                  path={menuItem.productPath}
                  onAddToCart={() => handleAddToCartWithToast(menuItem)}
                  onUpdateCartQuantity={
                    (quantity: number) => updateCartQuantity(menuItem, quantity)
                  }
                  productType={menuItem.productType}
                  skuId={menuItem.skuId}
                  isLtoBox={menuItem.isLtoBox}
                  ltoServings={menuItem.ltoServings}
                  description={menuItem.description}
                  handleSoldOutLtoClick={handleSoldOutLtoClick}
                  soldOut={showSoldOutOverlay}
                  isFavorite={menuItem.isFavorite}
                  isLoggedIn={isLoggedIn}
                  lazyLoadImage
                  productId={menuItem.productId}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
